import {Controller} from "@hotwired/stimulus"
import {get} from "@rails/request.js"

/**
 * Trigger an async CSV report by collecting params from one or more forms.
 *
 * @example
 * <button type="button"
 *         data-controller="async-download-csv"
 *         data-action="click->async-download-csv#prepareCsv"
 *         data-async-download-csv-prepare-csv-url-value="/path/to/prepare"
 *         data-async-download-csv-form-ids-value='["filter_form","search_form"]'>
 *   Generate CSV
 * </button>
 *
 * When multiple form ids are provided, later forms in the list win on
 * key collision — list the form carrying the most authoritative value last.
 */
export default class AsyncDownloadCsvController extends Controller {
  static values = {
    prepareCsvUrl: String,
    downloadCsvUrl: String,
    filename: String,
    formIds: Array,
  }

  prepareCsv() {
    const forms = this.resolveForms();

    if (forms.length === 0) {
      console.warn("AsyncDownloadCsvController: no forms resolved from", this.formIdsValue, "- aborting CSV request");
      return;
    }

    const params = new URLSearchParams();
    forms.forEach((form) => {
      new FormData(form).forEach((value, key) => {
        params.set(key, value);
      });
    });

    const url = this.prepareCsvUrlValue;
    const urlWithParams = `${url}?${params.toString()}&format=csv`;

    console.debug("AsyncDownloadCsvController: prepareCsv ->", urlWithParams);

    get(urlWithParams, {responseKind: "turbo-stream"});
  }

  resolveForms() {
    return this.formIdsValue
      .map((id) => document.getElementById(id))
      .filter((form) => form instanceof HTMLFormElement);
  }

  // Legacy path — retained per plan scope (Implementation Unit U1).
  doSubmit(form) {
    this.element.disabled = true;

    const params = new URLSearchParams(new FormData(form));
    const url = this.element.dataset.url || form.action;

    const urlWithParams = `${url}?${params.toString()}&format=csv`;

    get(urlWithParams, {responseKind: "blob"})
      .then(response => {
        if (response.ok) {
          return response.text;
        }
      })
      .then((content) => {
        this.element.disabled = false;

        if (content) {
          this.downloadCsv(content);
        }
      });
  }

  downloadCsv(content) {
    const filename = this.element.dataset.filename || "export.csv";
    const blob = new Blob([content], {type: "application/text"});
    const a = document.createElement("a");
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url)
  }
}
